import {
  BOARD_COLS,
  type Board,
  STARTER_BOARDS,
  type StarterBoard,
  type WidgetInstance,
} from "@kourai/api/home-board";
import { type KeyboardEvent, useCallback, useEffect, useMemo, useState } from "react";
import ReactGridLayout, { type Layout, useContainerWidth } from "react-grid-layout";
import { Link } from "react-router-dom";
import {
  addWidget,
  fromGrid,
  fromStarter,
  moveWidget,
  readingOrder,
  removeWidget,
  resizeWidget,
  restoreWidget,
  stepSize,
  toGrid,
  widgetId,
} from "@/lib/home/layout";
import { newInstance } from "@/lib/home/registry";
import type { WidgetSize } from "@/lib/home/widget";
import { cn } from "@/lib/utils";
import { widgets } from "@/widgets";
import { type TileContext, WidgetTile } from "./widget-tile";
import "./board.css";

/** Grid metrics: 8 columns, 88px rows, 16px gutters (S is 2×2, about 192px square at 1120px). */
const ROW_HEIGHT = 88;
const GUTTER = 16;
/** A widget's height for `h` rows, as on the grid: used where widgets stack in one column. */
export const stackedHeight = (h: number) => h * ROW_HEIGHT + (h - 1) * GUTTER;
/** Under this width the board is one column in reading order, and can't be dragged. */
const ONE_COLUMN_BELOW = 640;
/** How long "Removed … Undo" stays. */
const UNDO_MS = 8000;

export type HomeBoardProps = {
  board: Board;
  update: (edit: (board: Board) => Board) => void;
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
  workspace: { id: string; name: string };
  user: { id: string; name: string };
  /**
   * Onboarding (ADMIN-263): `tip` shows the one line Customize hint under the board,
   * `offerStarters` offers the built-in starters when Customize opens. Both once, then gone.
   */
  tip?: boolean;
  offerStarters?: boolean;
  onTipDismiss?: () => void;
  onStartersOffered?: () => void;
  /** The workspace published a starter this person hasn't answered (ADMIN-264). */
  starterOffer?: boolean;
  onStarterOffer?: (use: boolean) => void;
  /** Owners and admins: publish this board as the workspace's starter, or clear it. */
  manageStarter?: {
    hasStarter: boolean;
    publish: () => Promise<boolean>;
    clear: () => Promise<boolean>;
  };
  /**
   * Signed out: a look at the board people get, nothing on it works. Its content stays in memory,
   * and Customize (or a click anywhere on it) leads to sign in.
   */
  signedOut?: boolean;
};

/**
 * The board under the command box (ADMIN-258): widgets on an 8 column grid. At rest it's calm,
 * no handles and no grid lines. Customize shows the grid, a toolbar on each widget (drag, sizes,
 * remove), and a tray of widgets to add. Arrow keys move the focused widget, Shift+arrows resize it.
 */
export function HomeBoard({
  board,
  update,
  editing,
  onEditingChange,
  workspace,
  user,
  tip = false,
  offerStarters = false,
  onTipDismiss,
  onStartersOffered,
  starterOffer = false,
  onStarterOffer,
  manageStarter,
  signedOut = false,
}: HomeBoardProps) {
  const [starterNote, setStarterNote] = useState("");
  const { width, containerRef, mounted } = useContainerWidth({ initialWidth: 728 });
  const oneColumn = mounted && width < ONE_COLUMN_BELOW;
  const [removed, setRemoved] = useState<{ instance: WidgetInstance; title: string } | null>(null);
  const [announcement, setAnnouncement] = useState("");
  /** The starters strip in this Customize session, and the one shown on the board meanwhile. */
  const [starters, setStarters] = useState(false);
  const [preview, setPreview] = useState<StarterBoard | null>(null);

  // The first Customize offers the starters, once: seen counts even if it's closed untouched.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only when Customize opens
  useEffect(() => {
    if (!editing) {
      setStarters(false);
      setPreview(null);
      setStarterNote("");
      return;
    }
    if (offerStarters) {
      setStarters(true);
      onStartersOffered?.();
    }
  }, [editing]);

  const applyStarter = (s: StarterBoard) => {
    update(() => fromStarter(s.board));
    setPreview(null);
    setStarters(false);
    setAnnouncement(`Your board now starts from ${s.name}`);
  };

  useEffect(() => {
    if (!removed) return;
    const timer = setTimeout(() => setRemoved(null), UNDO_MS);
    return () => clearTimeout(timer);
  }, [removed]);

  const remove = useCallback(
    (instance: WidgetInstance) => {
      update((b) => removeWidget(b, instance.id));
      setRemoved({ instance, title: widgets.get(instance.type)?.title ?? "widget" });
    },
    [update],
  );

  const ctx = useMemo<TileContext>(
    () => ({
      workspace,
      user,
      editing,
      local: signedOut,
      onConfig: (id, config) =>
        update((b) => ({
          ...b,
          items: b.items.map((i) =>
            i.id === id
              ? { ...i, config, typeVersion: widgets.get(i.type)?.version ?? i.typeVersion }
              : i,
          ),
        })),
      onRemove: remove,
      onResize: (id, size: WidgetSize) => update((b) => resizeWidget(b, id, size)),
      onKey: (id, e: KeyboardEvent<HTMLElement>) => {
        if (e.target !== e.currentTarget) return; // keys inside a widget's own controls
        const dir = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[
          e.key
        ] as [number, number] | undefined;
        if (!dir) {
          if (e.key === "Delete" || e.key === "Backspace") {
            const instance = board.items.find((i) => i.id === id);
            if (instance) remove(instance);
            e.preventDefault();
          }
          return;
        }
        e.preventDefault();
        const item = board.items.find((i) => i.id === id);
        const def = item && widgets.get(item.type);
        if (!item || !def) return;
        if (e.shiftKey) {
          const grow = dir[0] > 0 || dir[1] > 0 ? 1 : -1;
          const next = stepSize(def.sizes, item, grow);
          if (!next) return;
          update((b) => resizeWidget(b, id, next));
          setAnnouncement(`${def.title} resized to ${next.toUpperCase()}`);
        } else {
          update((b) => moveWidget(b, id, dir[0], dir[1]));
          setAnnouncement(`Moved ${def.title}`);
        }
      },
    }),
    [workspace, user, editing, signedOut, update, board, remove],
  );

  const shown = preview?.board ?? board;
  /** A preview is shown as it would look at rest: nothing to drag or edit. */
  const shownCtx = useMemo(() => (preview ? { ...ctx, editing: false } : ctx), [ctx, preview]);
  const arranging = editing && !preview;
  const tiles = shown.items.filter((i) => i.type !== "kourai.command");
  const layout = useMemo(() => toGrid(shown), [shown]);
  const rows = layout.reduce((m, l) => Math.max(m, l.y + l.h), 0);

  const commit = useCallback(
    (next: Layout) => {
      update((b) => fromGrid(b, next));
    },
    [update],
  );

  const addable = widgets.addable("newtab").filter((def) => {
    const count = board.items.filter((i) => i.type === def.type).length;
    return def.max === undefined || count < def.max;
  });

  return (
    <div className="w-full pt-6">
      <div className="flex min-h-8 items-center justify-end gap-3 pb-2 text-[13px]">
        {signedOut ? (
          <>
            <span className="text-muted-foreground me-auto">
              Make this page yours with tasks, notes and links.
            </span>
            <Link to="/auth/signin" className="text-foreground hover:text-brand transition-colors">
              Sign in to customize
            </Link>
          </>
        ) : editing ? (
          <>
            <span className="text-muted-foreground me-auto hidden sm:inline">
              {starterNote ||
                "Drag a widget by its bar. Arrow keys move the focused one, Shift and arrows resize it."}
            </span>
            {manageStarter && arranging && (
              <>
                {manageStarter.hasStarter && (
                  <button
                    type="button"
                    onClick={async () => {
                      const ok = await manageStarter.clear();
                      setStarterNote(
                        ok
                          ? `New members of ${workspace.name} start from the default board again.`
                          : "Couldn't clear the starter. Try again.",
                      );
                    }}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Clear starter
                  </button>
                )}
                <button
                  type="button"
                  onClick={async () => {
                    const ok = await manageStarter.publish();
                    setStarterNote(
                      ok
                        ? `New members of ${workspace.name} start from this board. Others are offered it once.`
                        : "Couldn't set the starter. Try again.",
                    );
                  }}
                  className="text-muted-foreground hover:text-brand transition-colors"
                >
                  {manageStarter.hasStarter
                    ? "Update workspace starter"
                    : "Set as workspace starter"}
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => onEditingChange(false)}
              className="bg-primary text-primary-foreground hover:bg-brand px-3 py-1 transition-colors"
            >
              Done
            </button>
          </>
        ) : starterOffer ? (
          <>
            <span className="text-muted-foreground me-auto">
              {workspace.name} has a starter home. Use it or keep your own.
            </span>
            <button
              type="button"
              onClick={() => onStarterOffer?.(true)}
              className="text-foreground hover:text-brand transition-colors"
            >
              Use it
            </button>
            <button
              type="button"
              onClick={() => onStarterOffer?.(false)}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Keep mine
            </button>
          </>
        ) : tip ? (
          // The one time hint (ADMIN-263), on the board's own line so it's seen without scrolling.
          <>
            <span className="text-muted-foreground me-auto">
              Make this page yours. Move things around, or add your open tabs and prompts.
            </span>
            <button
              type="button"
              onClick={() => {
                onTipDismiss?.();
                onEditingChange(true);
              }}
              className="text-foreground hover:text-brand transition-colors"
            >
              Customize
            </button>
            <button
              type="button"
              onClick={onTipDismiss}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Not now
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => onEditingChange(true)}
            className="text-muted-foreground hover:text-brand transition-colors"
          >
            Customize
          </button>
        )}
      </div>

      {editing && starters && (
        <StarterPicker
          preview={preview}
          onPreview={setPreview}
          onUse={applyStarter}
          onKeep={() => {
            setPreview(null);
            setStarters(false);
          }}
        />
      )}

      <div
        ref={containerRef}
        className={cn("relative", signedOut && "group/preview")}
        // One empty row under the widgets while customizing, to drag into.
        style={
          arranging && !oneColumn ? { minHeight: (rows + 1) * (ROW_HEIGHT + GUTTER) } : undefined
        }
      >
        {signedOut && (
          <Link
            to="/auth/signin"
            className="absolute inset-0 z-10 flex items-center justify-center"
            aria-label="Sign in to use your board"
          >
            <span className="bg-primary text-primary-foreground px-3 py-1.5 text-sm font-medium opacity-0 transition-opacity group-hover/preview:opacity-100 group-focus-within/preview:opacity-100">
              Sign in to use your board
            </span>
          </Link>
        )}
        <div inert={signedOut || undefined}>
          {oneColumn ? (
            <div className="flex flex-col gap-8">
              {readingOrder(shown).map((item) => (
                <div key={item.id} style={{ height: stackedHeight(item.h) }}>
                  <WidgetTile instance={item} def={widgets.get(item.type)} ctx={shownCtx} />
                </div>
              ))}
            </div>
          ) : (
            <>
              {arranging && <GridLines width={width} rows={rows + 1} />}
              <ReactGridLayout
                width={width}
                layout={layout}
                gridConfig={{
                  cols: BOARD_COLS,
                  rowHeight: ROW_HEIGHT,
                  margin: [GUTTER, GUTTER],
                  containerPadding: [0, 0],
                }}
                dragConfig={{
                  enabled: arranging,
                  handle: ".board-drag-handle",
                  cancel: "button, select, input, textarea, a",
                }}
                resizeConfig={{ enabled: false }}
                onDragStop={commit}
                className={cn(editing && "select-none")}
              >
                {tiles.map((item) => (
                  <div key={item.id}>
                    <WidgetTile instance={item} def={widgets.get(item.type)} ctx={shownCtx} />
                  </div>
                ))}
              </ReactGridLayout>
            </>
          )}
        </div>
      </div>

      {arranging && (
        <fieldset className="border-border mt-6 flex flex-wrap items-center gap-2 border-t pt-3 text-[13px]">
          <legend className="sr-only">Add a widget</legend>
          <span aria-hidden className="text-muted-foreground me-1">
            Add
          </span>
          {addable.length === 0 && (
            <span className="text-muted-foreground">Every widget is on the board.</span>
          )}
          {addable.map((def) => (
            <button
              key={def.type}
              type="button"
              title={def.description}
              onClick={() => {
                update((b) => addWidget(b, newInstance(def, widgetId())));
                setAnnouncement(`Added ${def.title}`);
              }}
              className="border-input hover:border-foreground border border-dashed px-2.5 py-1 hover:border-solid"
            >
              + {def.title}
            </button>
          ))}
        </fieldset>
      )}

      {removed && (
        <div
          role="status"
          className="border-foreground bg-background fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-4 border px-4 py-2 text-sm"
        >
          <span>Removed {removed.title}.</span>
          <button
            type="button"
            onClick={() => {
              const { instance } = removed;
              update((b) => restoreWidget(b, instance));
              setRemoved(null);
            }}
            className="underline underline-offset-4 hover:text-brand"
          >
            Undo
          </button>
        </div>
      )}

      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </div>
  );
}

/**
 * The built-in starters, offered the first time Customize opens. Picking one shows it on the
 * board as it would look; "Use" makes it yours, "Keep this one" goes back to what you had.
 */
function StarterPicker({
  preview,
  onPreview,
  onUse,
  onKeep,
}: {
  preview: StarterBoard | null;
  onPreview: (s: StarterBoard | null) => void;
  onUse: (s: StarterBoard) => void;
  onKeep: () => void;
}) {
  return (
    <section
      aria-label="Start from a starter"
      className="border-foreground mb-4 flex flex-col gap-3 border-t pt-3 text-[13px]"
    >
      <p className="text-muted-foreground">
        Start from one of these, or keep the board you have. You can change anything after.
      </p>
      <div className="flex flex-wrap items-stretch gap-2">
        {STARTER_BOARDS.map((s) => (
          <button
            key={s.id}
            type="button"
            aria-pressed={preview?.id === s.id}
            onClick={() => onPreview(preview?.id === s.id ? null : s)}
            className={cn(
              "border-border hover:border-foreground flex w-40 flex-col gap-2 border p-2 text-start transition-colors",
              preview?.id === s.id && "border-foreground",
            )}
          >
            <MiniBoard board={s.board} />
            <span className="text-foreground text-sm">{s.name}</span>
            <span className="text-muted-foreground">{s.description}</span>
          </button>
        ))}
      </div>
      <div className="flex items-center gap-4">
        {preview && (
          <button
            type="button"
            onClick={() => onUse(preview)}
            className="bg-primary text-primary-foreground hover:bg-brand px-3 py-1 transition-colors"
          >
            Use {preview.name}
          </button>
        )}
        <button
          type="button"
          onClick={onKeep}
          className="text-muted-foreground hover:text-foreground"
        >
          Keep this one
        </button>
        {preview && <span className="text-muted-foreground">Showing {preview.name} below.</span>}
      </div>
    </section>
  );
}

/** A starter at a glance: its widgets as outlined blocks with their names. */
function MiniBoard({ board }: { board: Board }) {
  const items = board.items.filter((i) => i.type !== "kourai.command");
  const rows = items.reduce((m, i) => Math.max(m, i.y + i.h - 1), 1);
  return (
    <div aria-hidden className="relative w-full" style={{ aspectRatio: `${BOARD_COLS} / ${rows}` }}>
      {items.map((i) => (
        <div
          key={i.id}
          className="border-foreground/40 text-muted-foreground absolute overflow-hidden border px-1 text-[10px] leading-4"
          style={{
            left: `calc(${(i.x / BOARD_COLS) * 100}% + 1px)`,
            top: `calc(${((i.y - 1) / rows) * 100}% + 1px)`,
            width: `calc(${(i.w / BOARD_COLS) * 100}% - 2px)`,
            height: `calc(${(i.h / rows) * 100}% - 2px)`,
          }}
        >
          {widgets.get(i.type)?.title}
        </div>
      ))}
    </div>
  );
}

/** The cells behind the widgets while customizing. */
function GridLines({ width, rows }: { width: number; rows: number }) {
  const col = (width - GUTTER * (BOARD_COLS - 1)) / BOARD_COLS;
  const cells = [];
  for (let y = 0; y < rows; y++)
    for (let x = 0; x < BOARD_COLS; x++)
      cells.push(
        <div
          key={`${x}-${y}`}
          className="border-border absolute border"
          style={{
            left: x * (col + GUTTER),
            top: y * (ROW_HEIGHT + GUTTER),
            width: col,
            height: ROW_HEIGHT,
          }}
        />,
      );
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      {cells}
    </div>
  );
}
